import * as Effects from "redux-saga/effects";

import * as AnalyseAction from "../../actions/analyseAction";
import * as UploadAction from "../../actions/uploadAction";
import * as JobAction from "../../actions/jobAction";
import windowsScan from "../../../services/WindowsScan";
import { IStore } from "../../reducers";
import { JobStatus } from "../../../models/JobStatus";
import { IAnalyseDoubleSidedUploadRequest, IAnalyseManualJobRequest, IAnalyseRetrialRequest, IAnalyseScanRequest, IAnalyseUploadRequest } from "../../../models";
import uuid from 'react-native-uuid';
import { leaveBreadcrumb, logError } from '../../../utils/tracker';
import { isElectron, shouldUseLocalProcessing } from "../../../utils/miscUtils";

const put: any = Effects.put;
const takeEvery: any = Effects.takeEvery;
const all: any = Effects.all;
const fork: any = Effects.fork;
const select: any = Effects.select;
const call: any = Effects.call;

export const getGrainType = (store: IStore) => store.grainType.grainId;
export const getNetworkStatus = (store: IStore) => store.app.isOnline;
export const getProfileId = (store: IStore) => store.grainProfile.profileId;
export const getPreferredProcessingMode = (store: IStore) => store.settings.preferredProcessingMode;
export const getUsername = (store: IStore) => store.user.userProfile.response?.username;

const getBrowserOnline = (): boolean | undefined => {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean') {
            return navigator.onLine;
        }
    } catch {
        // ignore
    }
    return undefined;
};

export function* initiateScan(action: ReturnType<typeof AnalyseAction.initiateScan.request>): any {
    const request: IAnalyseScanRequest = action.payload;
    try {
        console.log('web initiate scan')
        yield put(JobAction.setJobId(request.jobId));
        const isOnline: boolean = yield select(getNetworkStatus);
        const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
        const browserOnline = getBrowserOnline();
        const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);
        const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
        if (useLocalProcessing && isElectron()) {
            const grainId = yield select(getGrainType);
            const profileId = yield select(getProfileId);
            const username = yield select(getUsername);
            const nowEpochSeconds = Math.floor(Date.now() / 1000);
            yield put(JobAction.upsertOptimisticUserJob({
                jobId: request.jobId,
                grainId,
                profileId,
                jobStatus: JobStatus.JOB_IMAGE_SCANNING,
                processingMode: 'local',
                createdAt: nowEpochSeconds,
                updatedAt: nowEpochSeconds,
                username,
                source: request.source,
                location: request.location,
                info: { location: request.location }
            }));
        }
        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_SCANNING,
        }));
        const imageData = yield call(windowsScan.scanImageWindows, {
            dpi: 600,
            colorMode: 'color',
            pageSize: 'A4',
            format: 'jpg'
        });

        // Update status to indicate scanning is complete and uploading is starting
        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_UPLOADING,
        }));
        if (useLocalProcessing && isElectron()) {
            const grainId = yield select(getGrainType);
            const profileId = yield select(getProfileId);
            const username = yield select(getUsername);
            const nowEpochSeconds = Math.floor(Date.now() / 1000);
            yield put(JobAction.upsertOptimisticUserJob({
                jobId: request.jobId,
                grainId,
                profileId,
                jobStatus: JobStatus.JOB_IMAGE_UPLOADING,
                processingMode: 'local',
                createdAt: nowEpochSeconds,
                updatedAt: nowEpochSeconds,
                username,
                source: request.source,
                location: request.location,
                info: { location: request.location }
            }));
        }

        yield put(UploadAction.upload.request({
            jobId: request.jobId,
            isBase64: true,
            dataObject: imageData,
            source: 'image',
            location: request.location
        }))
    } catch (error: any) {
        console.log(error)
        try {
            const isOnline: boolean = yield select(getNetworkStatus);
            const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
            const browserOnline = getBrowserOnline();
            const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);
            const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
            if (useLocalProcessing && isElectron()) {
                const grainId = yield select(getGrainType);
                const profileId = yield select(getProfileId);
                const username = yield select(getUsername);
                const nowEpochSeconds = Math.floor(Date.now() / 1000);
                yield put(JobAction.upsertOptimisticUserJob({
                    jobId: request.jobId,
                    grainId,
                    profileId,
                    jobStatus: JobStatus.JOB_FAILED,
                    processingMode: 'local',
                    createdAt: nowEpochSeconds,
                    updatedAt: nowEpochSeconds,
                    username,
                    source: request.source,
                    location: request.location,
                    info: { location: request.location }
                }));
            }
        } catch {
            // best-effort only
        }
        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_SCAN_ERROR,
        }));
        yield put(AnalyseAction.initiateScan.failure());
    }
}

export function* initiateUpload(action: ReturnType<typeof AnalyseAction.initiateUpload.request>): any {
    try {
        const request: IAnalyseUploadRequest = action.payload;
        yield put(JobAction.setJobId(request.jobId));
        yield put(UploadAction.upload.request({
            isBase64: false,
            dataObject: request.dataObject,
            jobId: request.jobId,
            source: request.source,
            location: request.location
        }));
    } catch (error: any) {
        yield put(AnalyseAction.initiateUpload.failure());
    }
}

export function* initiateDoubleSidedUpload(action: ReturnType<typeof AnalyseAction.initiateDoubleSidedUpload.request>): any {
    try {
        const request: IAnalyseDoubleSidedUploadRequest = action.payload;
        yield put(JobAction.setJobId(request.jobId));
        yield put(UploadAction.uploadDoubleSided.request({
            isBase64: false,
            dataObject: request.dataObject,
            jobId: request.jobId,
            source: request.source,
            location: request.location
        }));
    } catch (error: any) {
        yield put(AnalyseAction.initiateDoubleSidedUpload.failure());
    }
}

export function* initiateDoubleSidedScan(action: ReturnType<typeof AnalyseAction.initiateDoubleSidedScan.request>): any {
    const request: IAnalyseScanRequest = action.payload;
    try {
        leaveBreadcrumb('Starting initiateDoubleSidedScan saga', { jobId: request.jobId }, 'process');

        // Set job ID
        yield put(JobAction.setJobId(request.jobId));
        const isOnline: boolean = yield select(getNetworkStatus);
        const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
        const browserOnline = getBrowserOnline();
        const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);
        const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
        if (useLocalProcessing && isElectron()) {
            const grainId = yield select(getGrainType);
            const profileId = yield select(getProfileId);
            const username = yield select(getUsername);
            const nowEpochSeconds = Math.floor(Date.now() / 1000);
            yield put(JobAction.upsertOptimisticUserJob({
                jobId: request.jobId,
                grainId,
                profileId,
                jobStatus: JobStatus.JOB_IMAGE_SCANNING,
                processingMode: 'local',
                createdAt: nowEpochSeconds,
                updatedAt: nowEpochSeconds,
                username,
                source: request.source,
                location: request.location,
                info: { location: request.location },
                productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS',
            }));
        }

        // Update status to scanning
        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_SCANNING,
        }));

        // Scan from both devices
        const imageFilePaths = yield call(windowsScan.scanImageDoubleSided, {
            dpi: 600,
            colorMode: 'color',
            pageSize: 'A4',
            format: 'jpg'
        });

        leaveBreadcrumb('Double-sided scan complete', {
            jobId: request.jobId,
            imageCount: imageFilePaths?.length
        }, 'state');

        // Update status to uploading
        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_UPLOADING,
        }));
        if (useLocalProcessing && isElectron()) {
            const grainId = yield select(getGrainType);
            const profileId = yield select(getProfileId);
            const username = yield select(getUsername);
            const nowEpochSeconds = Math.floor(Date.now() / 1000);
            yield put(JobAction.upsertOptimisticUserJob({
                jobId: request.jobId,
                grainId,
                profileId,
                jobStatus: JobStatus.JOB_IMAGE_UPLOADING,
                processingMode: 'local',
                createdAt: nowEpochSeconds,
                updatedAt: nowEpochSeconds,
                username,
                source: request.source,
                location: request.location,
                info: { location: request.location },
                productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS',
            }));
        }

        // Dispatch to uploadDoubleSided saga with file paths
        yield put(UploadAction.uploadDoubleSided.request({
            jobId: request.jobId,
            isBase64: false,  // CRITICAL: false for file paths (saga will read files)
            dataObject: imageFilePaths,  // Array of file paths: [path1, path2]
            source: 'image',
            location: request.location
        }));

        yield put(AnalyseAction.initiateDoubleSidedScan.success(true));
    } catch (error: any) {
        logError(error, { context: 'initiateDoubleSidedScan', jobId: request.jobId });
        console.log('initiateDoubleSidedScan error:', error);

        try {
            const isOnline: boolean = yield select(getNetworkStatus);
            const preferredProcessingMode: 'local' | 'cloud' | undefined = yield select(getPreferredProcessingMode);
            const browserOnline = getBrowserOnline();
            const effectiveOnline = isOnline && (!isElectron() || browserOnline !== false);
            const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
            if (useLocalProcessing && isElectron()) {
                const grainId = yield select(getGrainType);
                const profileId = yield select(getProfileId);
                const username = yield select(getUsername);
                const nowEpochSeconds = Math.floor(Date.now() / 1000);
                yield put(JobAction.upsertOptimisticUserJob({
                    jobId: request.jobId,
                    grainId,
                    profileId,
                    jobStatus: JobStatus.JOB_FAILED,
                    processingMode: 'local',
                    createdAt: nowEpochSeconds,
                    updatedAt: nowEpochSeconds,
                    username,
                    source: request.source,
                    location: request.location,
                    info: { location: request.location },
                    productName: 'DOUBLE_SIDED_GRAIN_ANALYSIS',
                }));
            }
        } catch {
            // best-effort only
        }

        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_IMAGE_SCAN_ERROR,
        }));
        yield put(AnalyseAction.initiateDoubleSidedScan.failure());
    }
}

export function* initiateManualJob(action: ReturnType<typeof AnalyseAction.initiateManualJob.request>): any {
    try {
        const request: IAnalyseManualJobRequest = action.payload;
        yield put(JobAction.setJobId(request.jobId));

        const isOnline: boolean = yield select(getNetworkStatus);
        if (!isOnline) {
            yield put(JobAction.updateRpiJobStatus({
                jobId: request.jobId,
                status: JobStatus.JOB_FAILED,
            }));
            throw new Error('Internet connection required to create manual-entry jobs.');
        }

        yield put(JobAction.updateRpiJobStatus({
            jobId: request.jobId,
            status: JobStatus.JOB_CREATING_MANUAL_ENTRY,
        }));

        yield put(JobAction.createJob.request({
            jobId: request.jobId,
            source: 'manual',
            manualEntryInput: request.manualJobEntryInput,
            location: request.location
        }))
    } catch (error: any) {
        yield put(AnalyseAction.initiateManualJob.failure());
    }
}

export function* initiateTestJob(action: ReturnType<typeof AnalyseAction.initiateTestJob.request>): any {
    try {
        const grainId = yield select(getGrainType);
        const jobId = uuid.v4().toString();
        yield put(JobAction.setJobId(jobId));
        yield put(JobAction.createJob.request({
            jobId: jobId,
            test: true,
            object_key: `testImages/${grainId}/1.jpg`,
            source: 'image',
            location: ''
        }))
        yield put(JobAction.queueJob.request({
            jobId: jobId
        }))
    } catch (error: any) {
        yield put(AnalyseAction.initiateUpload.failure());
    }
}

export function* initiateRetrial(action: ReturnType<typeof AnalyseAction.initiateRetrial.request>): any {
    try {
        const request: IAnalyseRetrialRequest = action.payload;
        yield put(JobAction.queueJob.request({
            jobId: request.jobId
        }))
    } catch (error: any) {
        yield put(AnalyseAction.initiateUpload.failure());
    }
}

/*
* WATCHERS
*/

export function* watchInitiateScan() {
    yield takeEvery(AnalyseAction.initiateScan.request, initiateScan);
}

export function* watchInitiateUpload() {
    yield takeEvery(AnalyseAction.initiateUpload.request, initiateUpload);
}

export function* watchInitiateDoubleSidedUpload() {
    yield takeEvery(AnalyseAction.initiateDoubleSidedUpload.request, initiateDoubleSidedUpload);
}

export function* watchInitiateDoubleSidedScan() {
    yield takeEvery(AnalyseAction.initiateDoubleSidedScan.request, initiateDoubleSidedScan);
}

export function* watchInitiateManualJob() {
    yield takeEvery(AnalyseAction.initiateManualJob.request, initiateManualJob);
}

export function* watchInitiateTestJob() {
    yield takeEvery(AnalyseAction.initiateTestJob.request, initiateTestJob);
}

export function* watchInitiateRetrial() {
    yield takeEvery(AnalyseAction.initiateRetrial.request, initiateRetrial);
}

export default function* root() {
    yield all([fork(watchInitiateScan),
    fork(watchInitiateUpload),
    fork(watchInitiateDoubleSidedUpload),
    fork(watchInitiateDoubleSidedScan),
    fork(watchInitiateManualJob),
    fork(watchInitiateRetrial),
    fork(watchInitiateTestJob)]);
}
